/**
 * Schedule time maths.
 *
 * Every stored time is a ZONE-LESS LOCAL string ("2026-09-05T09:00") because
 * a tournament runs on the wall clock at the fields, not on UTC. Passing one
 * through `toISOString()` shifts a Sunday 09:00 game into the previous day for
 * anyone west of Greenwich, so times are parsed into local date parts and
 * written back the same way. Generators, the retime planner and the sanity
 * checker all agree by going through here.
 */

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;

/** Local `Date` for a stored time, or null when it is not a time at all. */
export function parseLocal(iso: string): Date | null {
  const m = LOCAL_RE.exec(iso.trim());
  if (m !== null) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Rows written before this convention (or pasted with a zone suffix) still
  // have to parse: the schedule validator has always accepted anything
  // `Date.parse` understands.
  const fallback = new Date(iso);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** The stored form: local parts, minute precision, no zone. */
export function formatLocal(d: Date): string {
  const pad = (n: number): string => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `iso` moved by a signed number of minutes, still zone-less. */
export function shiftLocal(iso: string, minutes: number): string | null {
  const d = parseLocal(iso);
  if (d === null) return null;
  return formatLocal(new Date(d.getTime() + minutes * 60_000));
}

/** Minutes from `from` to `to` (negative when `to` is earlier). */
export function minutesBetween(from: string, to: string): number | null {
  const a = parseLocal(from);
  const b = parseLocal(to);
  if (a === null || b === null) return null;
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

/** Sort key that keeps malformed times last instead of scrambling the list. */
export function timeKey(iso: string): number {
  return parseLocal(iso)?.getTime() ?? Number.POSITIVE_INFINITY;
}
